import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

interface ExcelImportError {
  rowNumber: number;
  errorMessage: string;
}

interface ExcelImportErrorDialogProps {
  open: boolean;
  onClose: () => void;
  errors: ExcelImportError[];
  successCount: number;
  errorCount: number;
  message: string;
}

const ExcelImportErrorDialog: React.FC<ExcelImportErrorDialogProps> = ({
  open,
  onClose,
  errors,
  successCount,
  errorCount,
  message
}) => {
  // 按行號分組錯誤
  const groupedErrors = React.useMemo(() => {
    const grouped = new Map<number, string[]>();

    errors.forEach(error => {
      const rowNum = error.rowNumber;
      if (!grouped.has(rowNum)) {
        grouped.set(rowNum, []);
      }

      // 將分號分隔的錯誤信息拆分並添加到該行
      const errorMessages = error.errorMessage.split(';').map(msg => msg.trim()).filter(msg => msg);
      grouped.get(rowNum)!.push(...errorMessages);
    });

    // 轉換為排序後的數組
    return Array.from(grouped.entries())
      .map(([rowNumber, errorMessages]) => ({
        rowNumber,
        errorMessages: Array.from(new Set(errorMessages)) // 去重
      }))
      .sort((a, b) => a.rowNumber - b.rowNumber); // 按行號排序
  }, [errors]);

  const formatErrorMessage = (errorMessages: string[]) => {
    return errorMessages.map((error, index) => (
      <Box key={index} sx={{ mb: 0.5 }}>
        <Typography variant="body2" color="error" sx={{ fontSize: '0.875rem' }}>
          • {error}
        </Typography>
      </Box>
    ));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          <Typography variant="h6" component="div">
            Excel 導入錯誤詳情
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* 錯誤摘要 */}
        <Box sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography variant="h6" gutterBottom>
            導入結果摘要
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`成功: ${successCount} 條`}
              color="success"
              variant="outlined"
              sx={{ color: 'success.contrastText', borderColor: 'success.contrastText' }}
            />
            <Chip
              label={`錯誤: ${errorCount} 條`}
              color="error"
              variant="outlined"
              sx={{ color: 'error.contrastText', borderColor: 'error.contrastText' }}
            />
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {message}
          </Typography>
        </Box>

        {/* 錯誤詳情表格 */}
        <TableContainer component={Paper} sx={{ maxHeight: '50vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{
                  fontWeight: 'bold',
                  bgcolor: 'grey.100',
                  minWidth: 100
                }}>
                  行號
                </TableCell>
                <TableCell sx={{
                  fontWeight: 'bold',
                  bgcolor: 'grey.100',
                  minWidth: 400
                }}>
                  錯誤詳情
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedErrors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      沒有錯誤記錄
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                groupedErrors.map((groupedError, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{
                      fontWeight: 'bold',
                      color: 'error.main',
                      verticalAlign: 'top',
                      pt: 2
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon color="error" fontSize="small" />
                        {groupedError.rowNumber}
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                      verticalAlign: 'top',
                      pt: 2,
                      pb: 2
                    }}>
                      {formatErrorMessage(groupedError.errorMessages)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          startIcon={<CloseIcon />}
        >
          關閉
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExcelImportErrorDialog;
